import Modal from "anchor-ui/modal";
import Button from "anchor-ui/button";
import Input from "anchor-ui/input";
import { SetStateAction, useEffect, useState } from "react";
import { Functions, httpsCallable } from "firebase/functions";
import List from "anchor-ui/list";
import ListItem from "anchor-ui/list-item";
import Avatar from "anchor-ui/avatar";
import defaultAvatar from "../../assets/default_avatar.jpg";
import { CometChat } from "@cometchat/chat-sdk-javascript";
import { getSecondaryColor } from "../../utils/activeColor";
import { getFontStyle } from "../../utils/getFontStyle";
import { getFullDateTimeFromTimestamp } from "../getDateFromTimestamp";

interface PaginationInfo {
  hasNextPage: boolean;
  lastVisibleId: string | null;
  pageSize: number;
}

interface SearchResponse {
  users: CometChat.User[];
  pagination: PaginationInfo;
}

const PAGE_SIZE = 15;

const SearchUserByIpModal = ({
  ipModalData,
  handleClose,
  setAlert,
  functions,
  setCurrentChat,
}: {
  ipModalData: {
    isOpen: boolean;
    ip: string;
  } | null;
  handleClose: () => void;
  setAlert: React.Dispatch<React.SetStateAction<{ message: string; type: string; } | null>>;
  functions: Functions;
  setCurrentChat: (chat: any) => void;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ipAddress, setIpAddress] = useState<string>(ipModalData?.ip ?? "");
  const [searchResults, setSearchResults] = useState<CometChat.User[]>([]);

  // Pagination states
  const [paginationHistory, setPaginationHistory] = useState<string[]>([]); // Store lastVisibleIds for "previous" navigation
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [lastVisibleId, setLastVisibleId] = useState<string | null>(null);

  // Firebase callable function
  const searchUserOnIpCallable = httpsCallable<
    {
      ip: string;
      pageSize: number;
      lastVisibleDocId: string | null;
    },
    SearchResponse
  >(functions, "searchUserOnIp");

  const handleSearch = async (lastVisibleDocId: string | null = null, isNewSearch: boolean = true) => {
    if (!ipAddress && isNewSearch) {
      setAlert({ message: "Vul een IP-adres in", type: "warning" });
      return;
    }

    // Remove all spaces from the IP address
    const sanitizedIpAddress = ipAddress.replace(/\s+/g, "");
    if (sanitizedIpAddress.length === 0) {
      setAlert({ message: "Vul een geldig IP-adres in", type: "warning" });
      return;
    }
    setIpAddress(sanitizedIpAddress);

    setIsLoading(true);

    // Only clear results if it's a new search
    if (isNewSearch) {
      setSearchResults([]);
      setPaginationHistory([]);
      setCurrentPage(0);
    }

    try {
      const result = await searchUserOnIpCallable({
        ip: ipAddress,
        pageSize: PAGE_SIZE,
        lastVisibleDocId: lastVisibleDocId
      });

      const { users, pagination } = result.data;

      // Filter out the users that are null or undefined
      const filteredUsers = users.filter((user: any) => user !== null && user !== undefined);

      // Convert Firebase users to CometChat users
      const cometChatUsers: CometChat.User[] = (filteredUsers || [])
        .map((user: any) => {
          let userObj = new CometChat.User(user.uid);
          userObj.setName(user.name || "Onbekende Gebruiker");
          userObj.setAvatar(user.avatar || defaultAvatar);
          userObj.setRole(user.role || "user");
          userObj.setMetadata(user.metadata || {});
          userObj.setStatus(user.status || "offline");
          userObj.setLastActiveAt(user.lastActiveAt || 0);
          return userObj;
        })
        .sort((a, b) => b.getLastActiveAt() - a.getLastActiveAt()); // Sort by lastActiveAt in descending order

      setSearchResults(cometChatUsers);
      setHasNextPage(pagination.hasNextPage);
      setLastVisibleId(pagination.lastVisibleId);

      if (cometChatUsers.length === 0 && isNewSearch) {
        setAlert({ message: `Geen gebruikers gevonden met IP-adres: ${ipAddress}`, type: "info" });
      }
    } catch (error: any) {
      console.error("Error searching user by IP:", error);
      setAlert({
        message: `Er is een fout opgetreden bij het zoeken naar gebruikers met IP-adres: ${ipAddress}`,
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextPage = () => {
    if (!hasNextPage) return;

    // Add current lastVisibleId to history for previous page navigation
    setPaginationHistory(prev => [...prev, lastVisibleId as string]);
    setCurrentPage(prev => prev + 1);

    // Fetch next page
    handleSearch(lastVisibleId, false);
  };

  const handlePreviousPage = () => {
    if (currentPage <= 0) return;

    // Calculate which lastVisibleDocId to use
    const newPage = currentPage - 1;
    const lastVisibleDocId = newPage === 0 ? null : paginationHistory[newPage - 1];

    // Remove the last item from history
    setPaginationHistory(prev => prev.slice(0, -1));
    setCurrentPage(newPage);

    // Fetch previous page
    handleSearch(lastVisibleDocId, false);
  };

  const handleResetAndClose = () => {
    setIpAddress("");
    setSearchResults([]);
    setPaginationHistory([]);
    setCurrentPage(0);
    setHasNextPage(false);
    setLastVisibleId(null);
    handleClose();
  };

  const handleOpenProfile = (user: CometChat.User) => {
    setCurrentChat((prev: any) => ({ ...prev, userProfile: user }));
    handleResetAndClose();
  };

  useEffect(() => {
    if (ipModalData?.ip) {
      setIpAddress(ipModalData.ip);
    }
  }, [ipModalData?.ip]);

  return (
    <Modal
      open={ipModalData?.isOpen ?? false}
      actions={
        <>
          <Button flatButton onClick={handleResetAndClose} disabled={isLoading}>
            SLUITEN
          </Button>
          <Button flatButton onClick={() => handleSearch(null, true)} disabled={isLoading || !ipAddress}>
            ZOEKEN
          </Button>
        </>
      }
      onClose={handleResetAndClose}
    >
      <h2>Zoek Gebruiker op IP-adres</h2>
      <p>Voer een IP-adres in om te zoeken naar bijbehorende gebruikers.</p>
      <br /><br />

      <div className="search-container">
        <Input
          label="IP-adres"
          type="text"
          value={ipAddress}
          onChange={(e: { target: { value: SetStateAction<string> } }) => setIpAddress(e.target.value)}
          maxLength={50}
          disabled={isLoading}
        />
      </div>

      <br />

      {isLoading && <p>Bezig met zoeken...</p>}

      {searchResults.length > 0 && (
        <div className="ip-results-container">
          <h3>Zoekresultaten:</h3>
          <List
            header={`Gebruikers gevonden (pagina ${currentPage + 1})`}
            style={{ backgroundColor: getSecondaryColor() }}
          >
            {searchResults.map((user) => (
              <ListItem
                key={user.getUid()}
                avatar={
                  <Avatar
                    image={user.getAvatar() || defaultAvatar}
                    status={user.getStatus()}
                    alt={user.getName() || "Gebruiker"}
                  />
                }
                style={{ backgroundColor: getSecondaryColor() }}
                primaryText={user.getName() || "Onbekende Gebruiker"}
                primaryTextStyle={getFontStyle("")}
                secondaryText={getFullDateTimeFromTimestamp(user.getLastActiveAt())}
                onClick={() => handleOpenProfile(user)}
              />
            ))}
          </List>

          {/* Pagination Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '16px'
          }}>
            <Button
              flatButton
              onClick={handlePreviousPage}
              disabled={isLoading || currentPage === 0}
            >
              Vorige
            </Button>

            <Button
              onClick={handleNextPage}
              disabled={isLoading || !hasNextPage}
            >
              Volgende
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default SearchUserByIpModal;
